/**
 * Promise.allSettled polyfill.
 *
 * On some mobile webviews the core-js compatibility layer pulled in by
 * `react-app-polyfill` (see src/index.tsx) replaces the global `Promise`
 * with an implementation that does not expose the static `allSettled`
 * method. Embedded third-party scripts (notably Google Tag Manager, which
 * calls `Promise.allSettled`) then throw
 * `TypeError: Promise.allSettled is not a function`.
 *
 * This module defines `Promise.allSettled` when it is missing. It must be
 * imported after the react-app-polyfill imports so that it augments the
 * final global `Promise`, whatever provides it.
 *
 * Ref: THPRT-6378 / Sentry PORTAL-20-96.
 */

type SettledResult<T> =
  | { status: 'fulfilled'; value: T }
  | { status: 'rejected'; reason: unknown }

export function allSettledPolyfill<T>(
  promises: Iterable<T | PromiseLike<T>>
): Promise<Array<SettledResult<T>>> {
  return Promise.all(
    Array.from(promises).map((item) =>
      Promise.resolve(item).then(
        (value): SettledResult<T> => ({ status: 'fulfilled', value }),
        (reason): SettledResult<T> => ({ status: 'rejected', reason })
      )
    )
  )
}

if (
  typeof Promise !== 'undefined' &&
  typeof (Promise as unknown as { allSettled?: unknown }).allSettled !== 'function'
) {
  ;(Promise as unknown as { allSettled: typeof allSettledPolyfill }).allSettled =
    allSettledPolyfill
}
